import React, { useEffect, useRef } from 'react';
import { styled } from 'styled-components';
import { Select, Option } from '@ingka/select';
import Text from '@ingka/text';
import { useTranslation } from 'react-i18next';
import analyticsApiClient from '../services/analytics';
import { useStateDispatch, useStateSelector } from '../store/';
import {
  clearSelectedTimeSlot,
  updateSelectedStore,
} from '../reducers/app/appReducer';
import {
  clearAvailableTimeSlots,
  loadTimeSlotsData,
} from '../reducers/timeslots/timeSlotsReducer';
import { type ServiceLocation } from '../helpers/appHelper';
import { sendEventToOptimizely } from '../optimizely';

const StyledHeading = styled(Text)`
  &&& {
    margin-bottom: 1rem;
    margin-top: 1rem;
  }
`;

const StyledSelect = styled(Select)`
  min-width: min(360px, 100%);
`;

interface StoreItem {
  text: string;
  value: string;
}

interface StorePickerPropTypes {
  stores: StoreItem[];
  serviceId: string;
  locationType?: ServiceLocation;
  operation?: string;
  isDisabled?: boolean;
}

const getTitleKey = (locationType?: ServiceLocation) => {
  return locationType === 'storeRemote'
    ? 'pages.timeslots.storeRemotePickerTitle'
    : locationType === 'storeHome'
      ? 'pages.timeslots.storeHomePickerTitle'
      : 'pages.timeslots.storePickerTitle';
};

const StorePicker: React.FC<StorePickerPropTypes> = ({
  stores,
  serviceId,
  locationType,
  operation,
  isDisabled,
}) => {
  const { country } = CSS_CONFIG;
  const { t } = useTranslation();
  const dispatch = useStateDispatch();
  const storeId = useStateSelector((state) => state.app.selected.storeId);
  const { sendEventToEpisod } = analyticsApiClient();

  const selectRef = useRef<HTMLSelectElement | null>(null);
  const pendingFocusRestore = useRef(false);

  useEffect(() => {
    if (!isDisabled && pendingFocusRestore.current) {
      selectRef.current?.focus();
      pendingFocusRestore.current = false;
    }
  }, [isDisabled]);

  const onStoreSelect = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    selectRef.current = event.currentTarget;
    pendingFocusRestore.current = true;
    const { value } = event.currentTarget;
    dispatch(updateSelectedStore(value));
    dispatch(clearAvailableTimeSlots());
    dispatch(clearSelectedTimeSlot());
    dispatch(loadTimeSlotsData({ storeId: value }));
    const eventName = `switching_store_${serviceId}_${operation}_appointment`;
    sendEventToEpisod(eventName);
    sendEventToOptimizely(eventName);
  };

  const headingTextStyle = country === 'kr' ? 'Heading.L' : 'Heading.M';

  return (
    <div>
      <StyledHeading tagName="h2" textStyle={headingTextStyle}>
        {t(getTitleKey(locationType))}
      </StyledHeading>
      <StyledSelect
        onChange={onStoreSelect}
        id="store"
        label={t('components.storePicker.label')}
        hintText=""
        value={storeId}
        disabled={isDisabled}
        data-cy="store-picker"
      >
        {stores.map((store: StoreItem) => (
          <Option name={store.text} value={store.value} key={store.value} />
        ))}
      </StyledSelect>
    </div>
  );
};

export default StorePicker;
